import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MixedObject } from '@core/interfaces/mixed-object';
import { ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery } from '@core/services/common/global.service';
import { getMousePosition } from '@modules/creator/components/special-block/lightbox/lightbox-helpers';
import { LightboxService } from '@modules/creator/components/special-block/lightbox/services/lightbox.service';
import { getStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-lightbox-preview-view',
  templateUrl: './lightbox-preview-view.component.html',
  styleUrls: ['./lightbox-preview-view.component.scss'],
  standalone: false,
})
export class LightboxPreviewViewComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() block: Block;
  @Input() isPreviewOpened: boolean;
  @ViewChildren('videoPlayer', {read: ElementRef}) videoPlayers: QueryList<ElementRef>;

  images: Block[] = [];
  descrptionsEnabled = false;
  mainPhotoContainer: Block;
  photoContainer: Block;
  leftArrow: Block;
  rightArrowContainer: Block;
  rightArrow: Block;
  closeIcon: Block;
  emptyBlock: Block;
  isMouseDownOnImage = false;
  shifting = false;
  private isZoomed = false;
  private mediaQuerySubscription: Subscription;
  private mouseDownPos = {x: 0, y: 0};
  private containerTranslateX = 0;
  private leftArrowBlockStyles: MixedObject = {};

  constructor(
    private lightBoxService: LightboxService,
    private globalService: GlobalService,
  ) {
    lightBoxService.openPreviewAtIndex.subscribe(index => {
      this.mainPhotoIndex = index;
    });
    this.mediaQuerySubscription = globalService.mediaQuery.subscribe((mediaQuery) => {
      this.generateBlockStyles(mediaQuery);
    });
  }

  _mainPhotoIndex = 0;

  get mainPhotoIndex() {
    return this._mainPhotoIndex + this.indexShift;
  }

  set mainPhotoIndex(value: number) {
    this._mainPhotoIndex = value;
  }

  get imageContainerStyle() {
    const xPosPercent = -(this.mainPhotoIndex * (100 / this.images.length));
    const xPos = `calc(${xPosPercent}% + ${this.containerTranslateX}px)`;
    return {
      width: `${(100 * this.images.length)}%`,
      transform: `translate(${xPos}, 0)`,
    };
  }

  get imageHandlerStyle() {
    return {
      width: `${(100 / this.images.length)}%`,
    };
  }

  get leftArrowAdditionalStyle() {
    const {
      basic: {width},
    } = this.leftArrowBlockStyles;
    if (!width) {
      return {};
    }
    return {
      flex: `1 ${width}`,
    };
  }

  private get indexShift(): number {
    return this.images.length > 1 ? 1 : 0;
  }

  ngOnInit(): void {
    this.mainPhotoContainer = this.block.children.find(item => item.settings.viewPart === ViewPart.MAIN_PHOTO_CONTAINER);
    const [leftArrow, photoContainer, rightArrowContainer] = this.mainPhotoContainer.children;

    this.photoContainer = photoContainer;
    this.leftArrow = leftArrow;
    this.rightArrowContainer = rightArrowContainer;

    const [closeIcon, rightArrow, emptyBlock] = this.rightArrowContainer.children;

    this.rightArrow = rightArrow;
    this.closeIcon = closeIcon;
    this.emptyBlock = emptyBlock;

    this.descrptionsEnabled = typeof this.photoContainer.descrptionsEnabled === 'boolean' && this.photoContainer.descrptionsEnabled;

    const {children} = this.photoContainer;
    if (children.length > 1) {
      this.images = [
        children[children.length - 1],
        ...children,
        children[0],
      ];
    } else {
      this.images = children;
    }
    this.generateBlockStyles(this.globalService.mediaQuery.value);
    this.disableSelectionForElementsUnderPopup();

    document.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight') {
        this.nextImage();
      }
      if (e.key === 'ArrowLeft') {
        this.previousImage();
      }
    });
  }

  ngAfterViewInit() {
    // This ensures that the view is initialized before accessing video players
    this.stopAllVideo();
  }

  ngOnDestroy() {
    this.enableSelectionForElementsUnderPopup();
  }

  closePreview() {
    this.stopAllVideo();
    this.lightBoxService.closePreview();
  }

  nextImage() {
    if (this.images.length <= 1) {
      return;
    }
    this.stopAllVideo();
    if (this.mainPhotoIndex >= this.images.length - 1) {
      this.shifting = true;
      this.mainPhotoIndex = 0;
      setTimeout(() => {
        this.shifting = false;
        this._mainPhotoIndex++;
      });
    } else {
      this._mainPhotoIndex++;
    }
  }

  previousImage() {

    if (this.images.length <= 1) {
      return;
    }
    this.stopAllVideo();
    if (this.mainPhotoIndex === 0) {
      this.shifting = true;
      this._mainPhotoIndex = this.images.length - 3;
      setTimeout(() => {
        this.shifting = false;
        this._mainPhotoIndex--;
      });
    } else {
      this._mainPhotoIndex--;
    }
  }

  setIsZoomed(value: boolean) {
    this.isZoomed = value;
  }

  mouseMove($event) {
    if (!this.isMouseDownOnImage || this.isZoomed) {
      return;
    }
    const mouse = getMousePosition($event);
    this.containerTranslateX = mouse.x - this.mouseDownPos.x;
  }

  mouseDown($event) {
    this.isMouseDownOnImage = true;
    this.mouseDownPos = getMousePosition($event);
  }

  mouseUp() {
    this.isMouseDownOnImage = false;

    if (this.containerTranslateX >= 100) {
      this.previousImage();
    } else if (this.containerTranslateX <= -100) {
      this.nextImage();
    }
    this.containerTranslateX = 0;
  }

  private isPhotoBlock(block): boolean {
    const properViewPart = block.settings?.viewPart === ViewPart.PREVIEW_PHOTO_BLOCK;
    if (properViewPart) {
      return true;
    }
    const imgSnippetName = '<<var(fullUrl)>>';
    return block.snippetName === imgSnippetName
      || block.children.some(child => child.snippetName === imgSnippetName);
  }

  getPhotoBlock(photoWrapperData) {
    if (this.descrptionsEnabled) {
      let photoBlock = null;
      if (typeof photoWrapperData.children === 'object' && Array.isArray(photoWrapperData.children)) {
        for (const child of photoWrapperData.children) {
          if (this.isPhotoBlock(child)) {
            photoBlock = child;
            break;
          }
        }
      }
      if (!photoBlock) {
        return photoWrapperData;
      }
      return photoBlock;
    } else {
      // legacy mode
      return photoWrapperData;
    }
  }

  getStandardBlocks(photoWrapperData) {
    const blocks: any[] = [];
    if (typeof photoWrapperData.children === 'object' && Array.isArray(photoWrapperData.children)) {
      for (const child of photoWrapperData.children) {
        if (!this.isPhotoBlock(child)) {
          blocks.push(child);
        }
      }
    }
    return blocks;
  }

  stopAllVideo() {
    this.videoPlayers.forEach(videoEl => {
      if (videoEl.nativeElement) {
        const video = videoEl.nativeElement.querySelector('video');
        if (video && video.currentTime > 0) {
          video.pause();
          video.currentTime = 0;
          // video.src = video.src;
          video.load();
        }
      }
    });
  }

  private generateBlockStyles(mediaQuery: MediaQuery): void {
    const querySnakeCase = camelCase(mediaQuery.query);
    this.leftArrowBlockStyles = getStyles(this.leftArrow?.styles || {}, querySnakeCase);
  }

  private disableSelectionForElementsUnderPopup(): void {
    if (this.isPreviewOpened) {
      document.body.classList.add('lightboxOpen');
    }
    document.body.removeAttribute('tabindex');
  }

  private enableSelectionForElementsUnderPopup(): void {
    document.body.classList.remove('lightboxOpen');
    document.body.setAttribute('tabindex', '0');
  }

}
